/**
 * Vorlage „Anbraten“.
 *
 * Fleisch in der heißen Pfanne: Es zischt und raucht, unten bildet sich eine
 * dunkle Kruste. Dann wird gewendet, und die zweite Seite bräunt ebenso.
 */
import { ease, lerp, segment } from '../geometry.ts';
import { compact, faded, group } from '../scene.ts';
import type { SceneNode } from '../scene.ts';
import { sizzle, steam } from '../parts/effects.ts';
import { meatPiece } from '../parts/food.ts';
import { arcArrow, counter } from '../parts/stage.ts';
import { GRATE_Y, HEAT_LEVELS, HEAT_POWER, heatFromText, stove, stoveKnob } from '../parts/stove.ts';
import type { HeatLevel } from '../parts/stove.ts';
import { pan } from '../parts/vessels.ts';
import { choice, defineTemplate, textValue } from '../template.ts';

/** Pfanne. */
const PAN = { cx: 96, width: 124 } as const;
const RIM_Y = GRATE_Y - 13 - PAN.width * 0.1;
/** Höhe, auf der das Bratgut aufliegt. */
const FLOOR_Y = RIM_Y + 8;
const KNOB_X = 214;

/** Zeitplan. */
const TIME = {
  first: [0.04, 0.42],
  flip: [0.44, 0.56],
  second: [0.58, 0.86],
  reset: [0.9, 1],
} as const;

/** Lage der Stücke je Art. */
const ITEMS = {
  steak: [{ dx: -2, dy: 0, width: 66, height: 20 }],
  pieces: [
    { dx: -36, dy: 2, width: 22, height: 19 },
    { dx: -12, dy: -3, width: 22, height: 19 },
    { dx: 12, dy: 3, width: 22, height: 19 },
    { dx: 36, dy: -2, width: 22, height: 19 },
  ],
} as const;

/** Größte Krustendicke (Anteil der halben Höhe). */
const CRUST = 0.75;

/** Stellt das Bratgut zu einem Zeitpunkt dar; raw zeigt den Anfangszustand. */
function food(item: keyof typeof ITEMS, t: number, raw: boolean): SceneNode[] {
  return ITEMS[item].map((entry, index) => {
    const cx = PAN.cx + entry.dx;
    const cy = FLOOR_Y + entry.dy - entry.height / 2;
    if (raw) {
      return meatPiece(cx, cy, entry.width, entry.height, 0, 0);
    }
    const first = ease.outQuad(segment(t, ...TIME.first)) * CRUST;
    const second = ease.outQuad(segment(t, ...TIME.second)) * CRUST;
    const flipStart = TIME.flip[0] + index * 0.015;
    const flip = ease.inOutCubic(segment(t, flipStart, flipStart + 0.08));
    if (flip <= 0) {
      return meatPiece(cx, cy, entry.width, entry.height, 0, first);
    }
    if (flip >= 1) {
      return meatPiece(cx, cy, entry.width, entry.height, CRUST, second);
    }
    // Im Flug: Das Stück hebt ab und dreht sich halb um die Längsachse.
    const hop = Math.sin(flip * Math.PI) * 20;
    return group([meatPiece(cx, cy - hop, entry.width, entry.height, 0, first)], {
      transform: { rotate: 180 * flip, ox: cx, oy: cy - hop },
    });
  });
}

/** Vorlage „Anbraten“. */
export const stoveSearTemplate = defineTemplate({
  id: 'stove.sear',
  group: 'stove',
  title: 'Anbraten',
  description:
    'Das Fleisch brät in der heißen Pfanne, bis sich unten eine braune Kruste bildet. Dann wird es gewendet und auf der anderen Seite ebenso gebräunt.',
  durationSeconds: 7,
  keywords: [
    'anbraten',
    'scharf anbraten',
    'anbräunen',
    'rundherum',
    'von beiden seiten',
    'braten',
    'kruste',
  ],
  stillTime: 0.36,
  parameters: [
    choice('item', 'Bratgut', { steak: 'großes Stück', pieces: 'Würfel' }, 'steak'),
    choice('heat', 'Hitze', HEAT_LEVELS, 'high'),
  ],
  inferValues(text) {
    const lower = text.toLowerCase();
    const item = /würfel|gulasch|stücke|geschnetzelt|streifen/.test(lower)
      ? 'pieces'
      : /steak|schnitzel|filet|kotelett|braten/.test(lower)
        ? 'steak'
        : undefined;
    return { item, ...heatFromText(text) };
  },
  scene(values, t) {
    const item = textValue(values, 'item', 'steak') as keyof typeof ITEMS;
    const power = HEAT_POWER[textValue(values, 'heat', 'high') as HeatLevel];
    const vessel = pan(PAN.cx, RIM_Y, PAN.width);
    const burner = stove(PAN.cx, power, t);
    const reset = ease.inOutSine(segment(t, ...TIME.reset));
    const heat = lerp(0.5, 1, power);
    return compact([
      ...counter(),
      ...burner.back,
      ...vessel.back,
      // Gleiche Gruppenstruktur wie faded(1, …), damit die Schleife exakt schließt.
      reset > 0 ? group(food(item, t, true)) : null,
      faded(1 - reset, food(item, t, false)),
      ...vessel.front,
      ...burner.front,
      ...sizzle(PAN.cx, FLOOR_Y - 6, 46, heat, t, 3),
      ...steam(PAN.cx, FLOOR_Y - 22, t, { count: 3, spread: 26, height: 26, strength: 0.75 * heat }),
      stoveKnob(KNOB_X, power),
    ]);
  },
  hints() {
    return [arcArrow(PAN.cx, FLOOR_Y - 18, 30, 22, 200, 340)];
  },
});
